import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { ToastContext, useFetch } from '@reveni-io/reveni-ui'

import { decisionTypes } from '@/returns/components/OrderSelector/constants'
import returnsServices from '@/returns/services/services'
import routes from '@/Router/routes.constants'

import { buildAssignments } from './helpers'
import { transformRequestConfirmUnexpectedItems, transformResponseGetUnexpectedItems } from './transformers'

const useReviewAdditionalProducts = ({ returnId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { setToastInfo } = useContext(ToastContext)

  const {
    data: unexpectedItems = [],
    setData: setUnexpectedItems,
    loading: gettingUnexpectedItems,
    error: unexpectedItemsError,
  } = useFetch(() => returnsServices.getUnexpectedItems({ returnId }), {
    doInitialRequest: true,
    transformResponse: transformResponseGetUnexpectedItems,
  })

  const { fetchData: confirmUnexpectedItems, loading: confirming } = useFetch(returnsServices.confirmUnexpectedItems, {
    doInitialRequest: false,
    transformRequest: transformRequestConfirmUnexpectedItems,
  })

  useEffect(() => {
    if (unexpectedItemsError) {
      setToastInfo({ show: true, message: unexpectedItemsError.errorMessage, type: 'error' })
    }
  }, [unexpectedItemsError, setToastInfo])

  const detailUrl = location.state?.from?.pathname || routes.transactionDetailPage.replace(':id', returnId)

  const linkToOrder = (unexpectedItemId, order) => {
    setUnexpectedItems(prev => {
      return prev.map(item => {
        return item.id === unexpectedItemId ? { ...item, decision: { type: decisionTypes.link, order } } : item
      })
    })
  }

  const discard = unexpectedItemId => {
    setUnexpectedItems(prev => {
      return prev.map(item => {
        return item.id === unexpectedItemId ? { ...item, decision: { type: decisionTypes.discard } } : item
      })
    })
  }

  const allDecided = unexpectedItems.length > 0 && unexpectedItems.every(item => item.decision)

  const confirmDisabled = !allDecided || confirming

  const handleCancel = () => {
    navigate(detailUrl)
  }

  const handleConfirm = async () => {
    try {
      await confirmUnexpectedItems({ returnId, assignments: buildAssignments(unexpectedItems) })
      setToastInfo({ show: true, message: t('Productos inesperados procesados'), type: 'success' })
      navigate(detailUrl)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  return {
    unexpectedItems,
    gettingUnexpectedItems,
    linkToOrder,
    discard,
    confirming,
    confirmDisabled,
    handleCancel,
    handleConfirm,
    detailUrl,
  }
}

export default useReviewAdditionalProducts
